import { ArticulationEngine, defaultArticulationControlState } from "../audio/ArticulationEngine";
import { PhysicalModelLayer } from "../audio/PhysicalModelLayer";
import { ResonanceLayer } from "../audio/ResonanceLayer";
import { SampleEngine, type RenderVoiceOptions } from "../audio/SampleEngine";
import { TuningEngine } from "../audio/TuningEngine";
import type { NoteIntent } from "../audio/types";
import { getDefaultTuningId, getDefaultVariantId, getInstrumentById, getTuningById, getVariantById } from "../data/instruments";
import {
  DEFAULT_AUDIO_LAB_CHANNELS,
  DEFAULT_AUDIO_LAB_SAMPLE_RATE,
  type AudioLabTestCase
} from "./audioLabRegistry";

declare global {
  interface Window {
    webkitOfflineAudioContext?: typeof OfflineAudioContext;
    webkitAudioContext?: typeof AudioContext;
  }
}

export interface RenderInstrumentToWavOptions {
  instrumentId: string;
  note: string;
  articulation: string;
  durationSec: number;
  sampleRate?: number;
  channels?: number;
}

export interface EncodeAudioBufferToWavOptions {
  audioBuffer: AudioBuffer;
  channels?: number;
}

const tuningEngine = new TuningEngine();
const articulationEngine = new ArticulationEngine();
const sampleEngine = new SampleEngine();
const resonanceLayer = new ResonanceLayer();
const physicalModelLayer = new PhysicalModelLayer();

function getOfflineAudioContextConstructor(): typeof OfflineAudioContext {
  const constructor = window.OfflineAudioContext ?? window.webkitOfflineAudioContext;

  if (!constructor) {
    throw new Error("This browser does not support OfflineAudioContext.");
  }

  return constructor;
}

function getAudioContextConstructor(): typeof AudioContext {
  const constructor = window.AudioContext ?? window.webkitAudioContext;

  if (!constructor) {
    throw new Error("This browser does not support AudioContext.");
  }

  return constructor;
}

function resolveOrnament(instrumentId: string, articulation: string): string | undefined {
  const normalized = articulation.toLowerCase();

  if (["normal", "pluck", "strike", "open", "breath", "accent", "sustain", "dayan"].includes(normalized)) {
    return undefined;
  }

  if (normalized === "vibrato") {
    if (instrumentId === "gayageum") {
      return "nonghyeon";
    }

    if (instrumentId === "daegeum") {
      return "yoseong";
    }

    if (instrumentId === "erhu") {
      return "portamento";
    }

    if (instrumentId === "sitar") {
      return "meend";
    }

    return "micro_bend";
  }

  if (normalized === "bend") {
    return instrumentId === "sitar" ? "meend" : "micro_bend";
  }

  if (normalized === "pressure") {
    return instrumentId === "tabla" ? "bayan_pressure" : undefined;
  }

  if (normalized === "rim") {
    return instrumentId === "janggu" ? "chaepyeon" : undefined;
  }

  if (normalized === "muted" || normalized === "damped") {
    return "muted";
  }

  if (normalized === "both_open") {
    return "gung";
  }

  return articulation;
}

function createRenderVoiceOptions(articulation: string): RenderVoiceOptions {
  const controls = { ...defaultArticulationControlState };

  if (articulation === "buzz") {
    controls.buzzAmount = 0.8;
  }

  if (articulation === "nonghyeon" || articulation === "vibrato") {
    controls.nonghyeonDepth = 0.62;
  }

  if (articulation === "meend" || articulation === "bend") {
    controls.microBendAmount = 0.62;
  }

  if (articulation === "yoseong" || articulation === "cheong_accent") {
    controls.breathPressure = 0.76;
    controls.cheongExposure = 0.78;
  }

  if (articulation === "bayan_pressure" || articulation === "pressure") {
    controls.bayanPressure = 0.72;
  }

  if (articulation === "mouth_shape_xy" || articulation === "vocalization") {
    controls.mouthShapeY = 0.68;
    controls.vocalization = 0.62;
  }

  if (articulation === "ombak") {
    controls.ombakDepth = 0.75;
  }

  return {
    bowPressure: controls.bowPressure,
    bowSpeed: controls.bowSpeed,
    breathPressure: controls.breathPressure,
    cheongTension: controls.cheongTension,
    cheongExposure: controls.cheongExposure,
    buzzAmount: controls.buzzAmount,
    bayanPressure: controls.bayanPressure,
    neyMicrobend: controls.neyMicrobend,
    ombakDepth: controls.ombakDepth,
    saronTuningHint: controls.saronTuningHint,
    tremolo: articulation === "tremolo",
    nonghyeonDepth: controls.nonghyeonDepth,
    microBendAmount: controls.microBendAmount,
    meendMode: controls.meendMode,
    mouthShapeY: controls.mouthShapeY,
    circularHold: controls.circularHold,
    vocalization: controls.vocalization
  };
}

function resolvePhysicalModelControlValue(articulation: string): number | undefined {
  if (articulation === "nonghyeon" || articulation === "vibrato") {
    return 0.62;
  }

  if (articulation === "meend") {
    return 520;
  }

  if (articulation === "bayan_pressure" || articulation === "pressure") {
    return 0.72;
  }

  if (articulation === "micro_bend" || articulation === "bend") {
    return 0.42;
  }

  return undefined;
}

function writeString(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function writePcmSample(view: DataView, offset: number, sample: number): void {
  const clamped = Math.max(-1, Math.min(1, sample));

  view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
}

export async function renderInstrumentToAudioBuffer(options: RenderInstrumentToWavOptions): Promise<AudioBuffer> {
  const sampleRate = options.sampleRate ?? DEFAULT_AUDIO_LAB_SAMPLE_RATE;
  const channels = options.channels ?? DEFAULT_AUDIO_LAB_CHANNELS;
  const durationSec = Math.max(0.2, options.durationSec);
  const frameCount = Math.ceil(durationSec * sampleRate);
  const OfflineContext = getOfflineAudioContextConstructor();
  const context = new OfflineContext(channels, frameCount, sampleRate);
  const instrument = getInstrumentById(options.instrumentId);
  const variant = getVariantById(instrument.id, getDefaultVariantId(instrument.id));
  const tuning = getTuningById(instrument.id, getDefaultTuningId(instrument.id));
  const ornament = resolveOrnament(instrument.id, options.articulation);
  const intent: NoteIntent = {
    id: `${instrument.id}-${options.note}-${options.articulation}`,
    noteName: options.note,
    velocity: options.articulation === "muted" || options.articulation === "damped" ? 0.62 : 0.84,
    ornament,
    createdAt: 0
  };
  const tuned = tuningEngine.resolve(intent, tuning);
  const articulated = articulationEngine.shape(tuned, instrument, defaultArticulationControlState);
  const voice = sampleEngine.render(context, articulated, instrument, variant, createRenderVoiceOptions(options.articulation));
  const master = context.createGain();

  master.gain.value = 0.82;
  resonanceLayer.connect(context, voice.output, master, instrument);
  master.connect(context.destination);
  physicalModelLayer.apply(context, voice, articulated, instrument, variant, {
    controlValue: resolvePhysicalModelControlValue(options.articulation),
    meendMode: defaultArticulationControlState.meendMode,
    erhuPortamentoShape: defaultArticulationControlState.erhuPortamentoShape
  });

  if (options.articulation === "muted" || options.articulation === "damped") {
    voice.stop(Math.min(durationSec * 0.42, 0.72));
  } else {
    voice.stop(Math.max(0.08, durationSec - 0.45));
  }

  return context.startRendering();
}

export function encodeAudioBufferToWavBlob(options: EncodeAudioBufferToWavOptions): Blob {
  const { audioBuffer } = options;
  const outputChannels = Math.max(1, Math.min(2, options.channels ?? Math.min(2, audioBuffer.numberOfChannels)));
  const bytesPerSample = 2;
  const blockAlign = outputChannels * bytesPerSample;
  const byteRate = audioBuffer.sampleRate * blockAlign;
  const dataByteLength = audioBuffer.length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataByteLength);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataByteLength, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, outputChannels, true);
  view.setUint32(24, audioBuffer.sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataByteLength, true);

  let offset = 44;

  for (let frame = 0; frame < audioBuffer.length; frame += 1) {
    for (let channel = 0; channel < outputChannels; channel += 1) {
      const sourceChannel = Math.min(channel, audioBuffer.numberOfChannels - 1);
      const sample = audioBuffer.getChannelData(sourceChannel)[frame] ?? 0;

      writePcmSample(view, offset, sample);
      offset += bytesPerSample;
    }
  }

  return new Blob([view], { type: "audio/wav" });
}

export async function renderInstrumentToWavBlob(options: RenderInstrumentToWavOptions): Promise<Blob> {
  const audioBuffer = await renderInstrumentToAudioBuffer(options);

  return encodeAudioBufferToWavBlob({
    audioBuffer,
    channels: options.channels
  });
}

export async function renderTestCaseToAudioBuffer(testCase: AudioLabTestCase): Promise<AudioBuffer> {
  return renderInstrumentToAudioBuffer({
    instrumentId: testCase.instrumentId,
    note: testCase.note,
    articulation: testCase.articulation,
    durationSec: testCase.durationSec,
    sampleRate: testCase.sampleRate,
    channels: testCase.channels
  });
}

export async function playAudioBuffer(audioBuffer: AudioBuffer): Promise<void> {
  const AudioContextConstructor = getAudioContextConstructor();
  const context = new AudioContextConstructor();

  if (context.state === "suspended") {
    await context.resume();
  }

  const source = context.createBufferSource();
  const gain = context.createGain();

  source.buffer = audioBuffer;
  gain.gain.value = 0.86;
  source.connect(gain);
  gain.connect(context.destination);
  source.start();

  source.addEventListener(
    "ended",
    () => {
      void context.close();
    },
    { once: true }
  );
}
